<template>
	<div class="container-content">
		<Tabs class="container-header" @change="activeTab = $event"/>
		
		<Transition appear name="fade-up">
			<div class="control-section">
				<DeviceSelector
						v-model="selectedDeviceIds"
						:tvs="store.pairedTVs"
						:can-send-commands="canSendCommands"
				/>
				
				<Divider/>
				
				<QuickActions
						:disabled="!canSendCommands || selectedDeviceIds.length === 0"
						:spam-locked="spamLocked"
						:is-rebooting="isRebooting"
						:reboot-count="rebootCount"
						:is-home="isHome"
						@command="sendCommand"
						@start-reboot="startReboot"
						@cancel-reboot="cancelReboot"/>
			</div>
		</Transition>
		
		<Transition appear name="fade-up-delayed">
			<div class="apps-section">
				<AppsGrid
						:available-apps="availableApps"
						:alternate-apps="alternateApps"
						:disabled="!canSendCommands || selectedDeviceIds.length === 0 || spamLocked"
						:active-app-name="activeAppName"
						@launch="launchApp"/>
			</div>
		</Transition>
		
		<Toast :status="commandStatus"/>
		
		<!-- ID input/ select modal -->
		<Modal
				:show="showIdModal"
				:title="gamesList.length || publicGames.length
					? `Select a ${pendingApp?.label} ${pendingApp?.game_id_type?.replace(' ID', '')}`
					: `Enter a ${pendingApp?.label} ${pendingApp?.game_id_type}`"
				:_title="`${pendingApp?.game_id_type} Required`"
				:confirm-text="(publicGames.length || gamesList.length) ? 'hidden' : 'Verify'"
				:confirm-disabled="(publicGames.length || gamesList.length) ? false : !idInput.trim()"
				:app-icon="pendingApp?.icon_url"
				cancel-text="Cancel"
				@close="showIdModal = false"
				@cancel="showIdModal = false"
				@confirm="confirmLaunchWithId"
		>
			<template v-if="gamesList.length">
				<div v-if="publicGames.length">
					<p class="divider-label">
						LOCAL {{ pendingApp?.game_id_type?.replace(' ID', '').toUpperCase() }}S
					</p>
					<Divider/>
				</div>
				
				<div class="games-list">
					<button
							v-for="g in gamesList"
							:key="g.id"
							class="game-btn"
							:class="{ active: idInput === g.id }"
							@click="confirmLaunchWithId(g.id)">
						<div><strong>{{ g.name || pendingApp?.game_id_type }}</strong></div>
						<div><span class="mono">{{ g.id }}</span></div>
					</button>
				</div>
			</template>
			
			<template v-if="publicGames.length">
				<Divider/>
				<p class="divider-label">
					PUBLIC {{ pendingApp?.game_id_type?.replace(' ID', '').toUpperCase() }}S
				</p>
				<Divider/>
				
				<div class="games-list">
					<button
							v-for="g in publicGames"
							:key="g.id"
							class="game-btn"
							:class="{ active: idInput === g.id }"
							@click="confirmLaunchWithId(g.id)">
						<div><strong>{{ g.name || pendingApp?.game_id_type }}</strong></div>
						<div><span class="mono">{{ g.id }}</span></div>
					</button>
				</div>
			</template>
			
			<template v-if="!publicGames.length && !gamesList.length">
				<input
						ref="idInputEl"
						v-model="idInput"
						type="text"
						class="id-input mono"
						:placeholder="pendingApp?.game_id_type"
						@keyup.enter="confirmLaunchWithId()"/>
				<p class="error-text">{{ idError }}</p>
			</template>
		</Modal>
		
		<!-- Spam modal -->
		<Modal
				:show="showSpamModal"
				title="Hey!"
				confirm-text="Sorry..."
				cancel-text=""
				@close="showSpamModal = false"
				@confirm="showSpamModal = false"
		>
			<p>{{ currentSpamRemark }}</p>
			<p class="spam-remark-secondary">Buttons will re-enable in a few seconds.</p>
		</Modal>
	</div>
</template>


<script setup>
import {computed, ref, onMounted, watch, onUnmounted, nextTick} from 'vue'
import {useMainStore} from '../store/main'
import Icon from './Icon.vue'
import Modal from './Modal.vue'
import Divider from './Divider.vue'
import {doCommand, fetchGamesForSite, validateGameId, generateUUID} from '../utils/api.js'
import QuickActions from "./QuickActions.vue";
import AppsGrid from "./AppsGrid.vue";
import Toast from "./Toast.vue";
import DeviceSelector from "./DeviceSelector.vue";
import Tabs from "@/components/Tabs.vue";

const store = useMainStore()
const activeTab = ref('devices')
/* ------------- device-selector ------------- */
const selectedDeviceIds = ref([])
const emit = defineEmits(['update:selectedDevices', 'command-sent'])

watch(selectedDeviceIds, n => emit('update:selectedDevices', n), {deep: true})

// Automatically select a single online device if none selected
watch(
	() => store.pairedTVs,
	() => {
		const selectedId = selectedDeviceIds.value[0]
		
		// If current selection is still valid and online, keep it
		const currentTv = store.pairedTVs.find(tv => tv.id === selectedId)
		if (currentTv && currentTv.status !== 'offline' && currentTv.status !== 'loading') {
			return
		}
		
		// Otherwise, select the first online device (if any)
		const firstOnline = store.pairedTVs.find(tv => 
			tv.status !== 'offline' && tv.status !== 'loading'
		)
		if (firstOnline) {
			selectedDeviceIds.value = [firstOnline.id]
		} else {
			selectedDeviceIds.value = []
		}
	},
	{deep: true}
)

/* ------------- apps ------------- */
const availableApps = computed(() => (store.apps || []).filter(a => !a.status || a.status === 'PRD' || a.status === 'COMING SOON'))
const alternateApps = computed(() => (store.apps || []).filter(a => a.status && a.status !== 'PRD' && a.status !== 'COMING SOON'))

const canSendCommands = computed(() => {
	if (!selectedDeviceIds.value.length) return false
	const d = store.pairedTVs.find(tv => tv.id === selectedDeviceIds.value[0])
	return !['offline', 'loading'].includes(d?.status)
})

const isHome = computed(() => {
	if (!selectedDeviceIds.value.length) return false
	const d = store.pairedTVs.find(tv => tv.id === selectedDeviceIds.value[0])
	return d?.status === 'home'
})

const activeAppName = computed(() => {
	if (!selectedDeviceIds.value.length) return null
	const tv = store.pairedTVs.find(tv => tv.id === selectedDeviceIds.value[0])
	if (!tv) return null
	// Return status if it's a running app (not system status)
	if (tv.status && !['home', 'offline', 'loading'].includes(tv.status)) {
		return tv.status
	}
	return null
})

/* ------------- command / reboot ------------- */
const commandStatus = ref(null)
const isRebooting = ref(false)
const rebootCount = ref(0)
const inFlightCommands = ref(new Map())
const REBOOT_DURATION_S = 10
const COMMAND_COOLDOWN_MS = 600
let rebootTimer = null
let countdownInterval = null
let statusTimer = null

function startReboot() {
	isRebooting.value = true
	rebootCount.value = REBOOT_DURATION_S
	sendCommand('reboot')
	
	countdownInterval = setInterval(() => {
		rebootCount.value -= 1
		if (rebootCount.value <= 0) clearInterval(countdownInterval)
	}, 1000)
	
	rebootTimer = setTimeout(() => {
		isRebooting.value = false
		clearInterval(countdownInterval)
		rebootCount.value = 0
	}, REBOOT_DURATION_S * 1000)
}

function cancelReboot() {
	clearTimeout(rebootTimer)
	clearInterval(countdownInterval)
	isRebooting.value = false
	rebootCount.value = 0
	sendCommand('cancel_reboot')
}

async function sendCommand(command, params = {}) {
	if (checkSpam()) return
	if (!canSendCommands.value) return
	const now = Date.now()
	const lastFired = inFlightCommands.value.get(command) ?? 0
	if (now - lastFired < COMMAND_COOLDOWN_MS) return
	inFlightCommands.value.set(command, now)

	// Use WebSocket for quick action commands
	if (['return_home', 'toggle_menu', 'launch_app', 'reboot', 'cancel_reboot'].includes(command)) {
		const requestId = generateUUID()
		const appName = params?.app || null
		// Get serial number of selected device
		const tv = store.pairedTVs.find(t => t.id === selectedDeviceIds.value[0])
		const serialNumbers = tv?.serial_number ? [tv.serial_number] : []
		const sent = store.sendWebSocketCommand(command, params, requestId, appName, serialNumbers)
		const commandLabels = {
			'toggle_menu': 'Toggling Menu',
			'return_home': 'Returning Home',
			'reboot': 'Rebooting',
			'cancel_reboot': 'Cancelling Reboot'
		}
		let label
		if (command === 'launch_app') {
			label = `Launching ${appName || 'App'}`
		} else {
			label = commandLabels[command] || command
		}
		if (sent) {
			showCommandStatus('success', label)
		} else {
			showCommandStatus('error', `Failed: ${label}`)
			// Attempt to reconnect WebSocket for next command
			store.startStatusSocket()
		}
		return
	}

	const tvId = selectedDeviceIds.value[0]
	if (!tvId) return

	try {
		showCommandStatus('info',
				command === 'reboot' ? `Rebooting device…`
						: command === 'launch_app' ? `Launching on device…` : `Sending to device…`
		)

		const commandPromises = [(async () => {
			try {
				const requestId = generateUUID()
				const pendingPromise = store.registerPendingCommand(requestId, tvId)
				await doCommand(tvId, command, params, store.user.host_site_id, requestId)
				const result = await pendingPromise

				if (result.busy) {
					store.cancelPendingCommand(requestId)
					return {tvId, success: true, skipped: true, busy: false}
				}

				return pendingPromise
						.then(r => ({tvId, success: true, ...r}))
						.catch(err => ({tvId, success: false, error: err.message}))

				// const result = await doCommand(tvId, command, params, store.user.host_site_id)
				// return store.registerPendingCommand(result.request_id, tvId)
				// 		.then(r => ({tvId, success: true, ...r}))
				// 		.catch(err => ({tvId, success: false, error: err.message}))
			} catch (err) {
				return {tvId, success: false, error: `Send failed: ${err.message}`}
			}
		})()]
		
		const results = await Promise.all(commandPromises)
		const failed = results
				.filter(r => !r.success)
				.map(r => store.pairedTVs.find(t => t.id === r.tvId)?.name || `Device ${r.tvId}`)

		showCommandStatus(
				failed.length ? 'error' : 'success',
				failed.length ? `Failed: ${failed.join(', ')}` : 'Success!'
		)

		emit('command-sent', {command, success: !failed.length, failedDevices: failed, details: results})
	} catch (e) {
		console.error('[CONTROL] sendCommand error:', e)
		showCommandStatus('error', 'Something went wrong')
	}
}

/* ------------- ID modal / app launch ------------- */
const showIdModal = ref(false)
const idInput = ref('')
const gamesList = ref([])
const publicGames = ref([])
const idError = ref('')
const idInputEl = ref(null)
const pendingApp = ref(null)

watch(showIdModal, async val => {
	if (val && !gamesList.value.length) {
		await nextTick()
		idInputEl.value?.focus()
	}
})

async function launchApp(app) {
	if (!app.game_id_type?.trim()) {
		return sendCommand('launch_app', {app: app.name})
	}
	
	const fetchedGames = await fetchGamesForSite(app, store.user.host_site_id)
	
	// // For testing UI with multiple games + mix of public/local
	// const gamesPoker = [
	// 	{id: 2, name: 'buzztime trade show', is_public: true},
	// 	{id: 732569, name: 'Shea Test Table', is_public: false},
	// 	{id: 691425, name: 'tournament', is_public: true},
	// 	{id: 627913, name: "Sheaqa's Table", is_public: false},
	// 	{id: 1, name: 'Buzztime Global', is_public: true},
	// ]
	// const gamesBets = [
	// 	{"id": 63518, "name": "Rays @ Guardians", "league": "MLB", "game_status": "Pre-Game", "game_time": "2026-04-29T17:10:00"},
	// 	{"id": 63517, "name": "Angels @ White Sox", "league": "MLB", "game_status": "Pre-Game", "game_time": "2026-04-29T17:10:00"},
	// 	{"id": 63519, "name": "Mariners @ Twins", "league": "MLB", "game_status": "Pre-Game", "game_time": "2026-04-29T17:40:00"},
	// 	{"id": 63520, "name": "Yankees @ Rangers", "league": "MLB", "game_status": "Pre-Game", "game_time": "2026-04-29T18:35:00"},
	// 	{"id": 63521, "name": "Red Sox @ Blue Jays", "league": "MLB", "game_status": "Pre-Game", "game_time": "2026-04-29T19:07:00"}
	// ]
	// fetchedGames.games = [...fetchedGames.games, ...gamesPoker]
	
	// Immediately launch app with all game IDs fetched -- no game selection
	if (app.game_id_type.toUpperCase() === 'ALL') {
		const allIds = fetchedGames.games.map(g => g.id)
		return sendCommand('launch_app', {app: app.name, ids: allIds})
	}
	
	pendingApp.value = app
	idInput.value = ''
	idError.value = ''
	
	// TODO: Improve sorting. Below is Poker specific, Bets may have game_type field or more
	gamesList.value = fetchedGames.games.filter(g => g?.is_public !== true)
	publicGames.value = fetchedGames.games.filter(g => g?.is_public === true)
	showIdModal.value = true
}

async function confirmLaunchWithId(i = null) {
	const id = i ?? idInput.value.trim()
	if (!id) return
	
	const app = pendingApp.value
	if (!app) return

	if (i !== null) {
		showIdModal.value = false
		pendingApp.value = null
		await sendCommand('launch_app', {app: app.name, id})
		return
	}

	const validation = await validateGameId(app, id)
	if (validation.unavailable) {
		idError.value = 'Unable to validate ID'
		await nextTick(() => idInputEl.value?.focus())
		return
	}
	if (!validation.valid) {
		idError.value = 'Invalid ID – please try again'
		await nextTick(() => idInputEl.value?.focus())
		return
	}

	idError.value = ''
	showIdModal.value = false
	pendingApp.value = null
	await sendCommand('launch_app', {app: app.name, id})
}

function showCommandStatus(type, msg) {
	clearTimeout(statusTimer)
	commandStatus.value = {type, message: msg}
	statusTimer = setTimeout(() => (commandStatus.value = null), 3000)
}

/* ------------- spam guard ------------- */
const SPAM_WINDOW_MS = 3000
const SPAM_THRESHOLD = 5
const SPAM_TIMEOUT_MS = 5000
const spamTimestamps = ref([])
const spamLocked = ref(false)
const showSpamModal = ref(false)
const spamRemarks = [
	'Whoa there. The TVs are working on it.',
	"Pressing it harder won't make it faster.",
	'The button felt that. Several times.',
	'Easy tiger.',
	'The TVs are not a vending machine.',
	'Patience is a virtue. Just saying.',
	'They heard you the first time.',
	"This isn't a rhythm game.",
	"Careful, you'll wear out the pixels!",
	'Alright, chill button masher.',
	"If enthusiasm fixed it, we'd be done already.",
	'Still pressing? Bold strategy.',
	"You seem fun at elevators.",
	"Maybe it's buffering your determination.",
	'Ever tried... waiting?',
	'Buttons have feelings too, you know.',
	"We're flattered by your dedication.",
	"You'll get carpal tunnel before it loads.",
]
const currentSpamRemark = ref('')

function checkSpam() {
	const now = Date.now()
	spamTimestamps.value = spamTimestamps.value.filter(t => now - t < SPAM_WINDOW_MS)
	spamTimestamps.value.push(now)
	
	if (spamTimestamps.value.length >= SPAM_THRESHOLD) {
		spamTimestamps.value = []
		spamLocked.value = true
		currentSpamRemark.value = spamRemarks[Math.floor(Math.random() * spamRemarks.length)]
		showSpamModal.value = true
		cancelReboot()
		setTimeout(() => {
			spamLocked.value = false
			showSpamModal.value = false
		}, SPAM_TIMEOUT_MS)
		return true
	}
	return false
}

/* ------------- lifecycle ------------- */
let refreshInterval

onMounted(async () => {
	if (!store.apps.length) await store.loadApps()
	
	refreshInterval = setInterval(async () => {
		if (!store.apps.length) await store.loadApps()
	}, 30000)
	
	if (store.pairedTVs.length === 1) selectedDeviceIds.value = [store.pairedTVs[0].id]
})

onUnmounted(() => {
	if (refreshInterval) clearInterval(refreshInterval)
})
</script>


<style scoped>
.app-section h4 {
	margin-bottom: var(--spacing-md);
	color: var(--color-text);
}

.divider-label {
	font-size: var(--font-size-xs);
	letter-spacing: var(--spacing-xs);
	margin-bottom: var(--spacing-md);
}

.error-text {
	margin-top: var(--spacing-md);
	color: var(--color-error);
}

.id-input {
	width: 50%;
	letter-spacing: var(--spacing-xs);
	font-size: var(--font-size-xl);
	text-align: center;
	margin-top: var(--spacing-md);
	padding: var(--spacing-md);
	border: var(--border-thin) var(--color-border);
	border-radius: var(--border-radius-sm);
	background: var(--color-surface-2);
	color: var(--color-text);
	text-transform: uppercase;
}

.games-list {
	max-height: 50dvh;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: var(--spacing-sm);
}

.game-btn {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	padding: var(--spacing-md);
	background: var(--color-surface-light-2);
	border: var(--border-thin) var(--color-border);
	border-radius: var(--border-radius-sm);
	color: var(--color-text);
	cursor: pointer;
	transition: all var(--transition-fast);
	text-align: start;
	text-transform: capitalize;
}

.game-btn:active {
	background: var(--color-primary);
	border-color: var(--color-border-hover);
}

.spam-remark-secondary {
	margin-top: var(--spacing-md);
	font-size: var(--font-size-sm);
	color: var(--color-text-secondary);
}
</style>